import { Alert, Button, Group, Modal, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { AxiosProgressEvent } from 'axios';
import { getLogger } from 'loglevel';
import { useCallback, useState } from 'react';

import { Success, Warning } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import type { ErrorFileDownloadProps } from '@apple/utils/files/components/ErrorFileDownload';
import type { BulkUploadItem, BulkUploadResponse } from '@apple/utils/files/models/bulk-upload';

import { EXCEL_ALLOWED_FILE_TYPES, EXCEL_MAX_FILE_SIZE } from '../models/excel';
import { UploadFile } from './UploadFile';
import { UploadResults } from './UploadResults';

const log = getLogger('excel-upload');

export type BulkUploadProps<TBulkUploadItem extends BulkUploadItem> = {
	close: () => void;
	onUpload: (
		file: File,
		onUploadProgress?: (event: AxiosProgressEvent) => void,
	) => Promise<BulkUploadResponse<TBulkUploadItem>>;
	onSave: (fileId: number) => Promise<void>;
	title?: string;
	opened: boolean;
	maxFileSize?: number;
	allowedFileTypes?: string[];
	errorDownloadProps?: Omit<ErrorFileDownloadProps, 'fileId'>;
};

export function BulkUploadDialog<TBulkUploadItem extends BulkUploadItem>({
	close,
	onUpload,
	onSave,
	title,
	opened,
	maxFileSize = EXCEL_MAX_FILE_SIZE,
	allowedFileTypes = EXCEL_ALLOWED_FILE_TYPES,
	errorDownloadProps,
}: BulkUploadProps<TBulkUploadItem>) {
	const { t } = useTranslation('manage');
	const [file, setFile] = useState<File | undefined>();
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [uploadResults, setUploadResults] = useState<
		BulkUploadResponse<TBulkUploadItem> | undefined
	>();
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);

	const reset = useCallback(() => {
		setFile(undefined);
		setUploadResults(undefined);
		setProgress(0);
		setError(undefined);
	}, []);

	const handleSelectFile = useCallback(
		(file: File) => {
			reset();
			setFile(file);
		},
		[reset],
	);

	const handleClose = useCallback(() => {
		close();
		reset();
	}, [close, reset]);

	const handleUploadClick = useCallback(() => {
		async function upload() {
			setUploadResults(undefined);
			if (file === undefined) return;
			setUploading(true);
			try {
				const result = await onUpload(file, e => {
					setProgress((e.progress ?? 0) * 100);
				});
				setUploadResults(result);
			} catch (error) {
				setError(t('excelUpload.errors.invalidFormat'));
				log.error('Error uploading template', error);
			} finally {
				setUploading(false);
			}
		}

		void upload();
	}, [file, onUpload, t]);

	const handleContinueClick = useCallback(() => {
		async function save() {
			if (uploadResults === undefined) {
				return;
			}

			setError(undefined);
			setSaving(true);
			try {
				await onSave(uploadResults.fileId);
				notifications.show({
					message: t('excelUpload.results.success'),
					icon: <Success />,
					color: 'green',
					autoClose: 5000,
				});
				handleClose();
			} catch (error) {
				setError(t('common:error.generic'));
				log.error('Error saving updates', error);
			} finally {
				setSaving(false);
			}
		}

		void save();
	}, [handleClose, onSave, t, uploadResults]);

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			title={
				uploadResults ? t('excelUpload.results.title') : (title ?? t('excelUpload.title'))
			}
			closeOnEscape={false}
			closeOnClickOutside={false}
			size='xl'
		>
			<Stack p='md'>
				{!uploadResults ? (
					<UploadFile
						onSelectFile={handleSelectFile}
						progress={progress}
						uploading={uploading}
						allowedFileTypes={allowedFileTypes}
						maxFileSize={maxFileSize}
					/>
				) : (
					<UploadResults
						processing={saving}
						results={uploadResults}
						keyProperty={'rowNumber'}
						keyLabel={'row'}
						errorDownloadProps={
							errorDownloadProps
								? { ...errorDownloadProps, fileId: uploadResults.fileId }
								: undefined
						}
					/>
				)}
				{error && (
					<Alert color='red' icon={<Warning />}>
						{error}
					</Alert>
				)}
				<Group justify='space-between' gap='xl' mt='md'>
					<Button onClick={reset} disabled={!uploadResults}>
						{t('common:buttons.back')}
					</Button>
					<Group justify='flex-end' gap='xl'>
						<Button
							variant='light'
							disabled={uploading || saving}
							onClick={handleClose}
						>
							{t('common:buttons.cancel')}
						</Button>
						{!uploadResults ? (
							<Button
								loading={uploading}
								onClick={handleUploadClick}
								disabled={file === undefined}
							>
								{t('common:buttons.upload')}
							</Button>
						) : (
							<Button
								loading={saving}
								disabled={
									!uploadResults ||
									(uploadResults.numberOfUpdatedRows === 0 &&
										uploadResults.numberOfInsertedRows === 0 &&
										uploadResults.numberOfDeletedRows === 0)
								}
								onClick={handleContinueClick}
							>
								{t('excelUpload.buttons.continueWithTemplate')}
							</Button>
						)}
					</Group>
				</Group>
			</Stack>
		</Modal>
	);
}
